/**
 * The Little Bunny: Custom Search Drawer
 * Handles AJAX search results, pagination, and Enter key blocking
 */
class CustomSearchDrawer {
  constructor() {
    this.drawer = document.querySelector('hdt-search-drawer');
    if (!this.drawer) return;

    this.form = this.drawer.querySelector('form');
    this.input = this.drawer.querySelector('input[type="search"]');
    this.resultsContainer = this.drawer.querySelector('[data-ajax-results]');
    this.popularProducts = this.drawer.querySelector('[data-popular-products]');

    this.debounceTimer = null;
    this.abortController = null;
    this.currentQuery = '';
    this.filterDrawerOpen = false;
    this.dynamicSectionId = null;
    this.popularProductsLoaded = false;
    this.searchCache = new Map();

    // Cache static DOM elements to avoid layout thrashing
    this.headerEl = document.querySelector('.hdt-header') || document.querySelector('hdt-sticky-header') || document.querySelector('.hdt-section-header');
    this.footerEl = document.getElementById('shopify-section-footer') || document.getElementById('t4s-footer') || document.querySelector('.hdt-section-footer');

    this.init();
  }

  init() {
    if (!this.form || !this.input || !this.resultsContainer) return;

    // 1. Block Enter key from submitting form
    this.form.addEventListener('submit', (e) => e.preventDefault());

    // 2. Listen to input changes with debounce
    this.input.addEventListener('input', () => this.onInputChange());

    // 3. Event delegation for pagination/filter links
    // Use capture phase to intercept before wrapp-hdt-facet-url custom element
    this.resultsContainer.addEventListener('click', (e) => this.onResultsClick(e), true);

    // 4. Handle filter drawer open/close buttons
    this.resultsContainer.addEventListener('click', (e) => this.onFilterButtonClick(e));

    // 5. Handle filter form submit and input changes
    this.resultsContainer.addEventListener('submit', (e) => this.onFilterFormSubmit(e));
    this.resultsContainer.addEventListener('change', (e) => this.onFilterChange(e));

    // 6. Clear results when drawer closes
    this.drawer.addEventListener('close', () => this.onDrawerClose());

    // 7. Load popular products when drawer opens
    const dialog = this.drawer.querySelector('dialog');
    if (dialog) {
      // HACK: replace the browser's modal mode (showModal -> show) so the header stays clickable
      if (typeof dialog.showModal === 'function') {
        dialog.showModal = function() {
          this.show(); 
        };
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'open' && dialog.hasAttribute('open')) {
            this.updateDrawerPosition();
            this.loadPopularProducts();
          }
        });
      });
      observer.observe(dialog, { attributes: true });

      // Recalculate the position if the user resizes the browser while search is open
      window.addEventListener('resize', () => {
        if (dialog.hasAttribute('open')) {
          this.updateDrawerPosition();
        }
      });
    }
  }

  updateDrawerPosition() {
    const dialog = this.drawer.querySelector('dialog');
    if (!dialog) return;

    // Bottom edge of the main header
    let topOffset = 0;
    if (this.headerEl) {
      const rect = this.headerEl.getBoundingClientRect();
      topOffset = Math.max(0, rect.bottom);
    }
    
    // Space the real footer takes at the bottom of the viewport (when the page is scrolled)
    let bottomOffset = 0;
    if (this.footerEl) {
      const rect = this.footerEl.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.top > 0) {
        bottomOffset = window.innerHeight - rect.top;
      }
    }
    const mobileNav = document.querySelector('.hdt-navbar-mobile');
    if (mobileNav) {
      const rect = mobileNav.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.top > 0) {
        bottomOffset = Math.max(bottomOffset, window.innerHeight - rect.top);
      }
    }

    // TRICK: the search form is a Dialog (Modal) covering the body; users scroll inside the modal, so they would never see the real site footer.
    // Show the footer below the search results: clone the real footer markup into the end of the scroll area.
    if (this.footerEl) {
       const popularContainer = this.drawer.querySelector('.custom-popular-products-container');
       const ajaxContainer = this.drawer.querySelector('.custom-search-ajax-container');
       
       if (popularContainer && !popularContainer.querySelector('.drawer-cloned-footer')) {
           const drawerFooter1 = document.createElement('div');
           drawerFooter1.className = 'drawer-cloned-footer';
           drawerFooter1.innerHTML = this.footerEl.outerHTML;
           popularContainer.appendChild(drawerFooter1);
       }
       if (ajaxContainer && !ajaxContainer.querySelector('.drawer-cloned-footer')) {
           const drawerFooter2 = document.createElement('div');
           drawerFooter2.className = 'drawer-cloned-footer';
           drawerFooter2.innerHTML = this.footerEl.outerHTML;
           ajaxContainer.appendChild(drawerFooter2);
       }
    }

    document.documentElement.style.setProperty('--search-drawer-top', `${topOffset}px`);
    // Release the bottomOffset lock so the Dialog extends all the way to the page bottom
    document.documentElement.style.setProperty('--search-drawer-bottom', `0px`);
  }

  onInputChange() {
    clearTimeout(this.debounceTimer);
    const query = this.input.value.trim();

    if (query.length === 0) {
      this.showPopularProducts();
      this.currentQuery = '';
      return;
    }

    this.debounceTimer = setTimeout(() => {
      if (query !== this.currentQuery) {
        this.currentQuery = query;
        this.fetchResults(query);
      }
    }, 300);
  }

  async fetchResults(query, url = null) {
    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Build URL
    const fetchUrl = url || this.buildSearchUrl(query);

    // Show loading state
    this.resultsContainer.classList.add('loading');
    this.hidePopularProducts();
    this.resultsContainer.classList.remove('hidden');

    // Fast cache return
    if (this.searchCache.has(fetchUrl)) {
      this.injectAndBindResults(this.searchCache.get(fetchUrl));
      this.resultsContainer.classList.remove('loading');
      return;
    }

    try {
      const response = await fetch(fetchUrl, {
        signal: this.abortController.signal,
        cache: 'default' // Use browser's native get cache alongside our local map
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Extract section content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find section by ID pattern (supports both static and dynamic section IDs)
      let newContent = doc.querySelector('[id^="shopify-section-"][id*="custom_search_results"]') ||
        doc.querySelector('#shopify-section-custom-search-results') ||
        doc.querySelector('.custom-search-results-section');

      if (newContent) {
        this.searchCache.set(fetchUrl, newContent.innerHTML); // Save HTML to cache
        this.injectAndBindResults(newContent.innerHTML);

        // Store the dynamic section ID for future requests
        const sectionWrapper = newContent.closest('[id^="shopify-section-"]') || newContent;
        if (sectionWrapper.id && sectionWrapper.id.includes('template--')) {
          this.dynamicSectionId = sectionWrapper.id.replace('shopify-section-', '');
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search fetch error:', error);
        this.resultsContainer.innerHTML = `<div class="custom-search-empty"><p>Something went wrong. Please try again.</p></div>`;
      }
    } finally {
      this.resultsContainer.classList.remove('loading');
    }
  }

  injectAndBindResults(htmlContent) {
    this.resultsContainer.innerHTML = htmlContent;
    this.initializeFilterDrawer();
    this.resultsContainer.scrollTop = 0;
  }

  buildSearchUrl(query) {
    const sectionId = this.dynamicSectionId || 'custom-search-results';
    const params = new URLSearchParams({
      q: query,
      section_id: sectionId,
      type: 'product,article'
    });
    return `/search?${params.toString()}`;
  }

  onResultsClick(event) {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Skip product and article links - let them navigate normally
    if (href.includes('/products/') || href.includes('/blogs/')) return;

    // Check if pagination or filter link (including wrapp-hdt-facet-url elements)
    const isPaginationLink = link.closest('.hdt-pagination') || link.closest('wrapp-hdt-facet-url');
    const isSearchLink = href.includes('/search');

    if (isSearchLink || isPaginationLink) {
      // Stop event from reaching wrapp-hdt-facet-url custom element
      event.preventDefault();
      event.stopPropagation();

      // Build URL with section_id
      const url = new URL(href, window.location.origin);

      // Use dynamic section ID if available, otherwise fallback to static ID
      const sectionId = this.dynamicSectionId || 'custom-search-results';
      url.searchParams.set('section_id', sectionId);

      // Update current query from URL
      const newQuery = url.searchParams.get('q');
      if (newQuery) {
        this.currentQuery = newQuery;
      }

      this.fetchResults(null, url.toString());
    }
  }

  onFilterButtonClick(event) {
    const btn = event.target.closest('[aria-controls="search-filter-drawer"]');
    if (!btn) return;

    const filterDrawer = this.resultsContainer.querySelector('#search-filter-drawer');
    if (!filterDrawer) return;

    // Stop propagation to prevent global aria-controls handler from running
    // Global handler would toggle the drawer again, causing open->close->open cycle
    event.stopPropagation();
    event.preventDefault();

    // Check if this is a close button (inside the drawer header)
    const isCloseBtn = btn.closest('.hdt-dialog-drawer__header');

    if (isCloseBtn) {
      // Close the drawer
      this.closeFilterDrawer(filterDrawer);
    } else {
      // Open the drawer
      this.openFilterDrawer(filterDrawer);
    }
  }

  openFilterDrawer(filterDrawer) {
    // Use hdt-drawer custom element's open() for proper animation and styling
    const hdtDrawer = filterDrawer.closest('hdt-drawer');
    if (hdtDrawer && typeof hdtDrawer.open === 'function') {
      hdtDrawer.open();
    } else if (typeof filterDrawer.showModal === 'function') {
      filterDrawer.showModal();
    } else {
      filterDrawer.setAttribute('open', '');
    }
    this.filterDrawerOpen = true;
  }

  closeFilterDrawer(filterDrawer) {
    // Use hdt-drawer custom element's close() for proper animation and styling
    const hdtDrawer = filterDrawer.closest('hdt-drawer');
    if (hdtDrawer && typeof hdtDrawer.close === 'function') {
      hdtDrawer.close();
    } else if (typeof filterDrawer.close === 'function') {
      filterDrawer.close();
    } else {
      filterDrawer.removeAttribute('open');
    }
    this.filterDrawerOpen = false;
  }

  onFilterFormSubmit(event) {
    const form = event.target.closest('form[id^="hdt-facet-filters-form"]');
    const facetElement = event.target.closest('hdt-facet-filters-form');

    if (!form && !facetElement) return;

    event.preventDefault();
    const targetForm = form || facetElement.querySelector('form');
    if (targetForm) {
      this.applyFilters(targetForm);
    }
  }

  onFilterChange(event) {
    const input = event.target;
    // Only handle filter inputs inside facet form or custom element
    const form = input.closest('form[id^="hdt-facet-filters-form"]');
    const facetElement = input.closest('hdt-facet-filters-form');

    if (!form && !facetElement) return;

    const targetForm = form || facetElement.querySelector('form');
    if (!targetForm) return;

    // For checkboxes (like "In stock only"), apply immediately
    if (input.type === 'checkbox') {
      this.applyFilters(targetForm);
      return;
    }

    // Small delay for other inputs to allow multiple quick selections
    clearTimeout(this.filterChangeTimer);
    this.filterChangeTimer = setTimeout(() => {
      this.applyFilters(targetForm);
    }, 300);
  }

  applyFilters(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    // Build URL params from form data
    for (const [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }

    // Ensure we have the search query
    if (!params.has('q') && this.currentQuery) {
      params.set('q', this.currentQuery);
    }

    // Add section_id for AJAX (use dynamic ID if available)
    const sectionId = this.dynamicSectionId || 'custom-search-results';
    params.set('section_id', sectionId);

    const url = `/search?${params.toString()}`;

    // Close filter drawer if open
    const filterDrawer = this.resultsContainer.querySelector('#search-filter-drawer');
    if (filterDrawer && this.filterDrawerOpen) {
      this.closeFilterDrawer(filterDrawer);
    }

    this.fetchResults(null, url);
  }

  initializeFilterDrawer() {
    // Theme's DialogComponent already handles:
    // - Backdrop click to close
    // - ESC key to close
    // No custom handler needed - using native hdt-drawer behavior
  }

  showPopularProducts() {
    if (this.popularProducts) {
      this.popularProducts.classList.remove('hidden');
    }
    this.resultsContainer.innerHTML = '';
    this.resultsContainer.classList.add('hidden');
  }

  hidePopularProducts() {
    if (this.popularProducts) {
      this.popularProducts.classList.add('hidden');
    }
  }

  async loadPopularProducts() {
    if (this.popularProductsLoaded || !this.popularProducts) return;

    try {
      const response = await fetch('/collections/all?sort_by=best-selling&section_id=custom-popular-products');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.custom-popular-products');

      if (content) {
        this.popularProducts.innerHTML = content.outerHTML;
        this.popularProductsLoaded = true;

        // Upgrade custom elements for AJAX-loaded content
        if (window.customElements) {
          customElements.upgrade(this.popularProducts);
        }

        // Dispatch event for any listeners that need to initialize
        this.popularProducts.dispatchEvent(new CustomEvent('popular-products:loaded', { bubbles: true }));
      }
    } catch (error) {
      console.error('Failed to load popular products:', error);
      // Keep skeleton visible on error - non-critical feature
    }
  }

  onDrawerClose() {
    // Optional: Reset state when drawer closes
    // Uncomment below if you want to clear results on close
    // this.showPopularProducts();
    // this.input.value = '';
    // this.currentQuery = '';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CustomSearchDrawer());
} else {
  new CustomSearchDrawer();
}